import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Shield, ExternalLink, User, Copy } from 'lucide-react';
import { ReportButton } from '@/components/ReportButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';

interface PropertyContactSidebarProps {
  property: any;
  propertyId: string;
}

export function PropertyContactSidebar({ property, propertyId }: PropertyContactSidebarProps) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: `I would like to inquire about your property EthioRealEstates - ${(property?.reference_code || `ID${propertyId.slice(0, 8)}`).toUpperCase()}. Please contact me at your earliest convenience.`,
  });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);

  const contactPhone = property.contact_phone || '+251911000000';
  const contactName = property.contact_person || 'Property Agent';
  const agentLogo = property.agent_logo_url || property.agency_logo_url || property.logo_url || null;
  const agencyName = property.agency_name || property.developer_name || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        message: form.message || undefined,
        property_id: propertyId,
        source: 'website',
      });
      toast.success('Your inquiry has been sent! The agent will contact you shortly.');
      setForm({
        name: '',
        phone: '',
        email: '',
        message: `I would like to inquire about your property EthioRealEstates - ${(property?.reference_code || `ID${propertyId.slice(0, 8)}`).toUpperCase()}. Please contact me at your earliest convenience.`,
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send inquiry');
    }
    setSubmitting(false);
  };

  const copyNumber = (num: string) => {
    navigator.clipboard.writeText(num);
    toast.success('Number copied to clipboard');
  };

  return (
    <div className="space-y-4">
      {/* Agent Contact Card */}
      <Card className="border-2 border-primary/20">
        <CardContent className="p-5">
          {/* Agent info */}
          <div className="flex items-center gap-3 mb-4">
            {agentLogo ? (
              <img src={agentLogo} alt={contactName} className="h-12 w-12 rounded-full object-cover border" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                {contactName.charAt(0)}
              </div>
            )}
            <div>
              <div className="font-semibold text-sm">{contactName}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Shield className="h-3 w-3 text-primary" />Verified Agent
              </div>
            </div>
          </div>

          {/* Phone reveal with copy */}
          <div className="mb-4">
            {phoneRevealed ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium">Mobile</div>
                    <a href={`tel:${contactPhone}`} className="text-primary font-semibold text-lg hover:underline">{contactPhone}</a>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => copyNumber(contactPhone)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold"
                onClick={() => setPhoneRevealed(true)}>
                <Phone className="h-4 w-4 mr-2" />
                {contactPhone.slice(0, 7)}••••  — Show Number
              </Button>
            )}
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs" asChild>
              <a href={`tel:${contactPhone}`}><Phone className="h-3.5 w-3.5 mr-1" />Call</a>
            </Button>
            <Button className="bg-whatsapp hover:bg-whatsapp/90 text-whatsapp-foreground font-semibold text-xs" asChild>
              <a href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp
              </a>
            </Button>
            <Button variant="outline" className="font-semibold text-xs" asChild>
              <a href={`mailto:${property.contact_email || ''}`}><Mail className="h-3.5 w-3.5 mr-1" />Email</a>
            </Button>
          </div>

          {/* Contact form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="text-sm font-semibold mb-2">Send a Message</div>
            <div className="space-y-1.5">
              <Label htmlFor="sd-name" className="text-xs">Name <span className="text-destructive">*</span></Label>
              <Input id="sd-name" required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sd-email" className="text-xs">Email <span className="text-destructive">*</span></Label>
              <Input id="sd-email" type="email" required placeholder="Your email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sd-phone" className="text-xs">Phone <span className="text-destructive">*</span></Label>
              <div className="flex gap-2">
                <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">🇪🇹 +251</div>
                <Input id="sd-phone" type="tel" required placeholder="Phone number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 text-sm" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sd-message" className="text-xs">Message <span className="text-destructive">*</span></Label>
              <Textarea id="sd-message" rows={3} required value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="text-sm" />
            </div>

            {/* Role selector */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">I am a:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                {[
                  { value: 'buyer', label: 'Buyer / Tenant' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <div key={opt.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={opt.value} id={`sd-role-${opt.value}`} />
                    <Label htmlFor={`sd-role-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Keep informed */}
            <div className="flex items-center gap-2">
              <Checkbox id="sd-keep-informed" checked={keepInformed} onCheckedChange={(c) => setKeepInformed(!!c)} />
              <Label htmlFor="sd-keep-informed" className="text-xs cursor-pointer">Keep me informed about similar properties.</Label>
            </div>

            <Button
              type="submit"
              variant="outline"
              className="w-full h-10 font-bold text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 gap-2"
              disabled={submitting}
            >
              <Mail className="h-4 w-4" />
              {submitting ? 'Sending...' : 'SEND EMAIL'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Agency / Developer Card */}
      <Card>
        <CardContent className="p-5">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              {agencyName || 'Listed By'}
            </div>
            {agentLogo ? (
              <img src={agentLogo} alt={agencyName || 'Agency'} className="h-16 w-auto mx-auto mb-3 object-contain" />
            ) : (
              <div className="h-16 w-16 mx-auto mb-3 rounded-lg bg-muted flex items-center justify-center">
                <Building2Icon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            {property.subscription_tier && (
              <Badge variant="secondary" className="mb-2 text-[10px] uppercase">
                ⭐ {property.subscription_tier}
              </Badge>
            )}
            <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground mb-3">
              <User className="h-3.5 w-3.5" />
              <span>{contactName}</span>
            </div>
            <Button variant="outline" size="sm" className="w-full font-semibold text-xs">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Agency Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Useful Links */}
      <Card>
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 text-center">
            Useful Links
          </div>
          <div className="space-y-2">
            <Link to={`/buy?city=${property.city}`} className="block text-sm text-primary hover:underline">
              Property for sale in {property.city}
            </Link>
            {property.sub_city && (
              <Link to={`/buy?city=${property.city}&sub_city=${property.sub_city}`} className="block text-sm text-primary hover:underline">
                Property for sale in {property.sub_city}
              </Link>
            )}
            <Link to={`/rent?city=${property.city}`} className="block text-sm text-primary hover:underline">
              Property for rent in {property.city}
            </Link>
            <Link to={`/buy?property_type=${property.property_type}`} className="block text-sm text-primary hover:underline capitalize">
              {property.property_type}s for sale
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Report Property */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <ReportButton
            targetType="property"
            targetId={propertyId}
            variant="outline"
            className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold text-sm"
            label="Report this property"
          />
        </CardContent>
      </Card>

      {/* Safety tip */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-xs mb-1">Safety Tips</div>
              <ul className="text-[11px] text-muted-foreground space-y-0.5">
                <li>• Don't pay any advance without verifying</li>
                <li>• Meet the seller at a safe public place</li>
                <li>• Verify the property documents before paying</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Building2Icon(props: any) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
    </svg>
  );
}
